import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CsfQuickUploadButton } from '../components/CsfQuickUploadButton';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, categoryAPI, fileAPI, emailAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  User,
  Envelope,
  Phone,
  Buildings,
  FileText,
  Star,
  CheckCircle,
  WarningCircle,
  CircleNotch,
  Upload,
  MagnifyingGlass,
  DownloadSimple,
  Trash,
  FilePdf,
  Image,
  Bell,
  PaperPlaneTilt,
  ShieldWarning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DeleteAccountDialog } from '../components/auth/DeleteAccountDialog';

const ProfilePage = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const renewMode = searchParams.get('renew') === '1';
  const [loading, setLoading] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [searchCategory, setSearchCategory] = useState('');
  const [fiscalDoc, setFiscalDoc] = useState(null);
  const [fiscalVerification, setFiscalVerification] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    contact_email: '',
    business_name: '',
    fiscal_name: '',
    rfc: '',
    fiscal_regime: '',
    postal_code: '',
  });
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fiscalRegimes, setFiscalRegimes] = useState([]);

  // Normalizes any stored fiscal_regime value to the canonical "{code} — {name}" form
  // used by the Select catalog. Handles legacy hyphen "-" / em-dash "—" / code-only / raw text.
  const normalizeFiscalRegime = (raw, catalog) => {
    if (!raw || !catalog || catalog.length === 0) return raw || '';
    const str = String(raw).trim();
    // Try to extract a 3-digit SAT code anywhere in the string
    const codeMatch = str.match(/\b(\d{3})\b/);
    if (codeMatch) {
      const found = catalog.find((r) => r.code === codeMatch[1]);
      if (found) return `${found.code} — ${found.name}`;
    }
    // Fallback: match by name (case-insensitive, separator-insensitive)
    const stripped = str.replace(/[—-]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
    const byName = catalog.find((r) =>
      stripped.includes(r.name.toLowerCase().replace(/\s+/g, ' ').trim())
    );
    if (byName) return `${byName.code} — ${byName.name}`;
    return str;
  };

  useEffect(() => {
    // Load SAT fiscal regime catalog (public endpoint, no auth needed)
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fiscal/regimes`);
        if (res.ok) {
          const data = await res.json();
          setFiscalRegimes(data.regimes || []);
        }
      } catch (_) {
        // non-critical; field will fall back to raw value
      }
    })();
  }, []);
  const [emailConfigured, setEmailConfigured] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        contact_email: user.contact_email || user.email || '',
        business_name: user.business_name || '',
        fiscal_name: user.fiscal_name || '',
        rfc: user.rfc || '',
        fiscal_regime: normalizeFiscalRegime(user.fiscal_regime || '', fiscalRegimes),
        postal_code: user.postal_code || '',
      });
      setSelectedSpecialties(user.specialties || []);
      fetchProfileStatus();
      fetchCategories();
      fetchFiscalDoc();
      fetchEmailPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Re-normalize fiscal_regime once the SAT catalog finishes loading
  // (handles the race where `user` arrives before `fiscalRegimes`).
  useEffect(() => {
    if (fiscalRegimes.length === 0) return;
    setFormData((prev) => {
      const normalized = normalizeFiscalRegime(prev.fiscal_regime, fiscalRegimes);
      if (normalized === prev.fiscal_regime) return prev;
      return { ...prev, fiscal_regime: normalized };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalRegimes]);

  const fetchEmailPreferences = async () => {
    try {
      const [prefsRes, statusRes] = await Promise.all([
        emailAPI.getPreferences(),
        emailAPI.getStatus(),
      ]);
      setEmailPrefs(prefsRes.data);
      setEmailConfigured(statusRes.data.configured);
    } catch (error) {
      console.error('Failed to fetch email preferences:', error);
    }
  };

  const handleTogglePref = async (key) => {
    const newVal = !emailPrefs[key];
    setEmailPrefs(prev => ({ ...prev, [key]: newVal }));
    setSavingPrefs(true);
    try {
      const res = await emailAPI.updatePreferences({ [key]: newVal });
      setEmailPrefs(res.data);
      toast.success('Preferencia actualizada');
    } catch (error) {
      setEmailPrefs(prev => ({ ...prev, [key]: !newVal }));
      toast.error('Error al actualizar preferencia');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      await emailAPI.sendTest({ to_email: user.email, template: 'welcome' });
      toast.success(`Correo de prueba enviado a ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar correo de prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const fetchProfileStatus = async () => {
    try {
      const response = await userAPI.getProfileStatus();
      setProfileStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch profile status:', error);
    }
  };

  const fetchFiscalDoc = async () => {
    try {
      const response = await fileAPI.getFiscalDocumentInfo();
      if (response.data.has_document) {
        setFiscalDoc(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch fiscal document:', error);
    }
    // Load any stored verification result so the user keeps seeing the
    // failure message after a page refresh until they fix it.
    try {
      const me = await userAPI.getProfile();
      const status = me.data?.fiscal_verification_status;
      const errors = me.data?.fiscal_verification_errors;
      const expiresAt = me.data?.fiscal_constancy_expires_at;
      const expired = status === 'verified' && expiresAt && new Date(expiresAt) < new Date();
      if (status) {
        setFiscalVerification({
          status,
          verified: status === 'verified' && !expired,
          expired: !!expired,
          errors: expired
            ? [`Tu constancia venció el ${new Date(expiresAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}. Descarga una nueva del SAT y súbela.`]
            : (errors || []),
        });
      }
    } catch (_) { /* non-fatal */ }
  };

  const handleUploadFiscalDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('La Constancia de Situación Fiscal debe ser un PDF (descárgalo desde el portal del SAT).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe exceder 10MB');
      return;
    }

    setUploadingDoc(true);
    setFiscalVerification(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Use the validating endpoint — extracts text and matches against fiscal_data
      const res = await userAPI.uploadFiscalConstancy(formData);
      const verification = res.data?.verification;
      setFiscalVerification(verification);

      if (verification?.verified) {
        toast.success('Constancia fiscal validada y aceptada correctamente.');
      } else {
        // Validation FAILED — surface errors clearly so the user can fix them
        toast.error(
          'Tu constancia no pasó la validación. Revisa los datos marcados abajo y corrige tu información fiscal o sube una constancia actualizada.',
          { duration: 7000 },
        );
      }
      await fetchFiscalDoc();
      // Refresh user from server so role gating updates immediately
      try {
        const me = await userAPI.getProfile();
        if (updateUser) updateUser(me.data);
      } catch (_) { /* non-fatal */ }
    } catch (error) {
      // Surface structured validation errors from the backend in the red banner,
      // not just a toast. The backend may return either a string `detail` or an
      // object `detail: { message, errors: [...] }`.
      const detail = error?.response?.data?.detail;
      let message = 'Error al subir la constancia.';
      let errors = [];

      if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object') {
        if (detail.message) message = detail.message;
        if (Array.isArray(detail.errors)) errors = detail.errors;
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      }

      if (errors.length > 0) {
        // Show the banner with the specific validation errors
        setFiscalVerification({
          verified: false,
          errors,
        });
        toast.error(message, { duration: 7000 });
      } else {
        toast.error(message);
      }
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDownloadFiscalDoc = async () => {
    try {
      const response = await fileAPI.downloadFiscalDocument();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fiscalDoc?.filename || 'constancia_fiscal';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar el archivo');
    }
  };

  const handleDeleteFiscalDoc = async () => {
    setDeletingDoc(true);
    try {
      await fileAPI.deleteFiscalDocument();
      setFiscalDoc(null);
      toast.success('Documento eliminado');
    } catch (error) {
      toast.error('Error al eliminar el documento');
    } finally {
      setDeletingDoc(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await userAPI.completeProfile(formData);
      await refreshUser();
      await fetchProfileStatus();
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSpecialties = async () => {
    setLoading(true);
    try {
      await userAPI.updateSpecialties(selectedSpecialties);
      await refreshUser();
      toast.success('Especialidades actualizadas');
    } catch (error) {
      toast.error('Error al actualizar especialidades');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (newRole) => {
    setLoading(true);
    try {
      await userAPI.updateRole(newRole);
      await refreshUser();
      toast.success('Rol actualizado correctamente');
    } catch (error) {
      toast.error('Error al cambiar el rol');
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialty = (categoryName) => {
    setSelectedSpecialties(prev => 
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const filteredCategories = categories.filter(c => 
    c.name.toLowerCase().includes(searchCategory.toLowerCase())
  );

  const isProvider = user?.role === 'proveedor' || user?.role === 'ambos';

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Mi Perfil</h1>
          <p className="text-zinc-600">Gestiona tu información personal y profesional</p>
        </div>
      </div>

      {/* Profile Status Card */}
      {profileStatus && (
        <Card className={`rounded-none ${profileStatus.complete ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            {profileStatus.complete ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <WarningCircle className="w-8 h-8 text-amber-600" />
            )}
            <div className="flex-1">
              <h3 className={`font-semibold ${profileStatus.complete ? 'text-green-800' : 'text-amber-800'}`}>
                {profileStatus.complete ? 'Perfil Completo' : 'Perfil Incompleto'}
              </h3>
              {profileStatus.missing_fields?.length > 0 && (
                <p className="text-sm text-amber-700">
                  Campos faltantes: {profileStatus.missing_fields.join(', ')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" weight="fill" />
              <span className="font-bold">{user?.rating_avg?.toFixed(1) || '5.0'}</span>
              <span className="text-sm text-zinc-500">({user?.rating_count || 0})</span>
              <button
                type="button"
                onClick={() => navigate('/profile/ratings')}
                className="ml-2 text-xs text-orange-600 hover:text-orange-700 underline underline-offset-2 font-medium"
                data-testid="view-ratings-link"
              >
                Ver todas
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue={searchParams.get('tab') || 'info'} className="space-y-6">
        <TabsList className="bg-zinc-100 rounded-none p-1 border border-zinc-200">
          <TabsTrigger value="info" className="rounded-none data-[state=active]:bg-white">
            Información
          </TabsTrigger>
          <TabsTrigger value="fiscal" className="rounded-none data-[state=active]:bg-white">
            Datos Fiscales
          </TabsTrigger>
          {isProvider && (
            <TabsTrigger value="specialties" className="rounded-none data-[state=active]:bg-white">
              Especialidades
            </TabsTrigger>
          )}
          <TabsTrigger value="role" className="rounded-none data-[state=active]:bg-white">
            Rol
          </TabsTrigger>
          <TabsTrigger value="emails" className="rounded-none data-[state=active]:bg-white">
            Correos
          </TabsTrigger>
          <TabsTrigger value="legal" className="rounded-none data-[state=active]:bg-white" data-testid="profile-legal-tab">
            Legal
          </TabsTrigger>
        </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="info">
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nombre</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="rounded-none mt-1"
                    data-testid="profile-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <Input
                    id="email"
                    value={user?.email}
                    disabled
                    className="rounded-none mt-1 bg-zinc-100"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="rounded-none mt-1"
                    data-testid="profile-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Correo de Contacto</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    className="rounded-none mt-1"
                  />
                </div>
              </div>

              <Button 
                onClick={handleSaveProfile} 
                disabled={loading}
                className="btn-primary rounded-none"
                data-testid="save-profile-btn"
              >
                {loading ? <CircleNotch className="w-5 h-5 animate-spin" /> : 'Guardar Cambios'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fiscal Data Tab */}
        <TabsContent value="fiscal">
          {renewMode && (
            <div className="mb-4 border-2 border-orange-500 bg-orange-50 dark:bg-orange-950/30 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="csf-renew-card">
              <div className="flex-1">
                <p className="font-bold text-orange-900 dark:text-orange-200 uppercase tracking-wide text-sm">Renueva tu Constancia de Situación Fiscal</p>
                <p className="text-sm text-orange-800 dark:text-orange-300 mt-1">Elige el PDF que descargaste del SAT. Lo validamos al instante.</p>
              </div>
              <CsfQuickUploadButton testId="profile-csf-renew-btn" autoOpen onSuccess={() => { navigate('/profile?tab=fiscal', { replace: true }); fetchFiscalDoc(); }} />
            </div>
          )}
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="flex items-center gap-2">
                <Buildings className="w-5 h-5" />
                Datos Fiscales
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_name">Nombre Comercial</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => handleInputChange('business_name', e.target.value)}
                    className="rounded-none mt-1"
                    data-testid="profile-business-name"
                  />
                </div>
                <div>
                  <Label htmlFor="fiscal_name">Razón Social</Label>
                  <Input
                    id="fiscal_name"
                    value={formData.fiscal_name}
                    onChange={(e) => handleInputChange('fiscal_name', e.target.value)}
                    className="rounded-none mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="rfc">RFC</Label>
                  <Input
                    id="rfc"
                    value={formData.rfc}
                    onChange={(e) => handleInputChange('rfc', e.target.value.toUpperCase())}
                    className="rounded-none mt-1"
                    data-testid="profile-rfc"
                  />
                </div>
                <div>
                  <Label htmlFor="fiscal_regime">Régimen Fiscal <span className="text-xs text-zinc-400 font-normal">(SAT vigente)</span></Label>
                  <Select
                    value={formData.fiscal_regime || ''}
                    onValueChange={(v) => handleInputChange('fiscal_regime', v)}
                  >
                    <SelectTrigger
                      id="fiscal_regime"
                      className="rounded-none mt-1 h-10"
                      data-testid="profile-fiscal-regime-trigger"
                    >
                      <SelectValue placeholder="Selecciona tu régimen fiscal" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80" data-testid="profile-fiscal-regime-list">
                      {fiscalRegimes.length === 0 && formData.fiscal_regime && (
                        <SelectItem value={formData.fiscal_regime}>{formData.fiscal_regime}</SelectItem>
                      )}
                      {fiscalRegimes.map((r) => (
                        <SelectItem
                          key={r.code}
                          value={`${r.code} — ${r.name}`}
                          data-testid={`fiscal-regime-opt-${r.code}`}
                        >
                          <span className="font-mono text-xs text-zinc-500 mr-2">{r.code}</span>
                          <span>{r.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    Debe coincidir exactamente con el régimen de tu Constancia de Situación Fiscal.
                  </p>
                </div>
                <div>
                  <Label htmlFor="postal_code">Código Postal</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    className="rounded-none mt-1"
                  />
                </div>
              </div>

              <Button 
                onClick={handleSaveProfile} 
                disabled={loading}
                className="btn-primary rounded-none"
              >
                {loading ? <CircleNotch className="w-5 h-5 animate-spin" /> : 'Guardar Datos Fiscales'}
              </Button>
            </CardContent>
          </Card>

          {/* Fiscal Document Upload */}
          <Card className="border-zinc-200 rounded-none mt-6">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="flex items-center gap-2">
                <FilePdf className="w-5 h-5" />
                Constancia de Situación Fiscal
              </CardTitle>
              <p className="text-xs text-zinc-500 mt-1">
                PDF oficial del SAT, no mayor a 3 meses. La plataforma valida automáticamente que tus
                datos fiscales coincidan con el documento.
              </p>
            </CardHeader>
            <CardContent className="p-6">
              {/* Validation result banner */}
              {fiscalVerification && (
                <div
                  className={`mb-4 border-2 p-4 ${
                    fiscalVerification.verified
                      ? 'border-green-300 bg-green-50'
                      : 'border-red-400 bg-red-50'
                  }`}
                  data-testid="fiscal-verification-banner"
                >
                  <div className="flex items-start gap-3">
                    {fiscalVerification.verified ? (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" weight="fill" />
                    ) : (
                      <ShieldWarning className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" weight="fill" />
                    )}
                    <div className="flex-1 space-y-2">
                      <p
                        className={`font-bold uppercase tracking-wide text-sm ${
                          fiscalVerification.verified ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {fiscalVerification.verified
                          ? 'Constancia validada correctamente'
                          : fiscalVerification.expired
                            ? 'Tu constancia está vencida'
                            : 'Tu constancia no coincide con tus datos fiscales'}
                      </p>
                      {!fiscalVerification.verified && (
                        <>
                          {!fiscalVerification.expired && <p className="text-sm text-red-900 leading-relaxed">
                            Detectamos que algunos datos del PDF no coinciden con la información que
                            capturaste arriba. <strong>Para acceder a las cotizaciones disponibles
                            corrige los datos o sube una constancia actualizada.</strong>
                          </p>}
                          {Array.isArray(fiscalVerification.errors) && fiscalVerification.errors.length > 0 && (
                            <ul className="text-sm text-red-900 list-disc list-inside space-y-1 bg-white/60 p-3 border border-red-200">
                              {fiscalVerification.errors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                            </ul>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="rounded-none border-red-500 text-red-700 hover:bg-red-100"
                              onClick={() => {
                                document.getElementById('fiscal_data_form_top')?.scrollIntoView({ behavior: 'smooth' });
                              }}
                              data-testid="fiscal-fix-data-btn"
                            >
                              Corregir mis datos fiscales arriba
                            </Button>
                            <a
                              href="https://www.sat.gob.mx/aplicacion/login/53027/emite-tu-constancia-de-situacion-fiscal"
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-sm text-red-700 underline px-2"
                            >
                              Descargar constancia actualizada del SAT →
                            </a>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {fiscalDoc ? (
                <div className="border border-zinc-200 p-4" data-testid="fiscal-doc-info">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 flex items-center justify-center flex-shrink-0">
                      {fiscalDoc.content_type?.includes('pdf') ? (
                        <FilePdf className="w-5 h-5 text-orange-600" />
                      ) : (
                        <Image className="w-5 h-5 text-orange-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{fiscalDoc.filename}</p>
                      <p className="text-xs text-zinc-500">
                        {(fiscalDoc.size / 1024).toFixed(1)} KB 
                        {fiscalDoc.uploaded_at && ` • Subido el ${new Date(fiscalDoc.uploaded_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={handleDownloadFiscalDoc}
                        data-testid="download-fiscal-doc-btn"
                      >
                        <DownloadSimple className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={handleDeleteFiscalDoc}
                        disabled={deletingDoc}
                        data-testid="delete-fiscal-doc-btn"
                      >
                        {deletingDoc ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Trash className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-zinc-300 p-8 text-center" data-testid="fiscal-doc-dropzone">
                  <Upload className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                  <p className="text-sm text-zinc-700 font-medium mb-1">
                    Sube tu Constancia de Situación Fiscal
                  </p>
                  <p className="text-xs text-zinc-500 mb-4">
                    PDF, JPG, PNG o WebP. Máximo 10MB.
                  </p>
                  <label className="cursor-pointer">
                    <Button
                      asChild
                      variant="outline"
                      className="rounded-none border-orange-300 text-orange-600 hover:bg-orange-50"
                      disabled={uploadingDoc}
                    >
                      <span>
                        {uploadingDoc ? (
                          <CircleNotch className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingDoc ? 'Subiendo...' : 'Seleccionar Archivo'}
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={handleUploadFiscalDoc}
                      className="hidden"
                      data-testid="fiscal-doc-input"
                    />
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Specialties Tab */}
        {isProvider && (
          <TabsContent value="specialties">
            <Card className="border-zinc-200 rounded-none">
              <CardHeader className="border-b border-zinc-200">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Especialidades
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-zinc-600 mb-4">
                  Selecciona las categorías de materiales que puedes suministrar. 
                  Recibirás notificaciones de cotizaciones en estas categorías.
                </p>

                <div className="relative mb-4">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <Input
                    placeholder="Buscar categorías..."
                    value={searchCategory}
                    onChange={(e) => setSearchCategory(e.target.value)}
                    className="pl-10 rounded-none"
                  />
                </div>

                <div className="mb-4">
                  <span className="text-sm text-zinc-500">
                    {selectedSpecialties.length} seleccionadas
                  </span>
                </div>

                <ScrollArea className="h-[300px] border border-zinc-200 p-4">
                  <div className="grid sm:grid-cols-2 gap-2">
                    {filteredCategories.map((category) => (
                      <div 
                        key={category.category_id} 
                        className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                          selectedSpecialties.includes(category.name)
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-zinc-200 hover:bg-zinc-50'
                        }`}
                        onClick={() => toggleSpecialty(category.name)}
                      >
                        <Checkbox
                          checked={selectedSpecialties.includes(category.name)}
                          onCheckedChange={() => toggleSpecialty(category.name)}
                        />
                        <span className="text-sm">{category.name}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                <Button 
                  onClick={handleUpdateSpecialties} 
                  disabled={loading}
                  className="btn-primary rounded-none mt-4"
                  data-testid="save-specialties-btn"
                >
                  {loading ? <CircleNotch className="w-5 h-5 animate-spin" /> : 'Guardar Especialidades'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Role Tab */}
        <TabsContent value="role">
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle>Tipo de Cuenta</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-zinc-600 mb-6">
                Selecciona el tipo de cuenta que mejor se adapte a tus necesidades.
              </p>

              <div className="grid sm:grid-cols-3 gap-4">
                {['cliente', 'proveedor', 'ambos'].map((role) => (
                  <div
                    key={role}
                    className={`border p-6 cursor-pointer transition-all ${
                      user?.role === role
                        ? 'border-amber-400 bg-amber-50 shadow-[4px_4px_0px_0px_rgba(251,191,36,1)]'
                        : 'border-zinc-200 hover:border-zinc-400'
                    }`}
                    onClick={() => handleRoleChange(role)}
                  >
                    <h3 className="font-bold text-lg capitalize mb-2">{role}</h3>
                    <p className="text-sm text-zinc-600">
                      {role === 'cliente' && 'Compra materiales de construcción'}
                      {role === 'proveedor' && 'Vende materiales de construcción'}
                      {role === 'ambos' && 'Compra y vende materiales'}
                    </p>
                    {user?.role === role && (
                      <Badge className="mt-4 bg-amber-500 rounded-none">Actual</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Preferences Tab */}
        <TabsContent value="emails">
          <Card className="border-zinc-200 rounded-none" data-testid="email-preferences-card">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="flex items-center gap-2">
                <Envelope className="w-5 h-5" />
                Notificaciones por Correo
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {!emailConfigured && (
                <div className="bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
                  <WarningCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Servicio de correo no configurado</p>
                    <p className="text-xs text-amber-700 mt-1">Las notificaciones por correo no están disponibles en este momento.</p>
                  </div>
                </div>
              )}

              <p className="text-zinc-600 text-sm dark:text-zinc-400">
                Elige qué notificaciones deseas recibir en tu correo electrónico. Puedes activar o desactivar cada tipo de notificación.
              </p>

              {emailPrefs ? (
                <div className="space-y-1" data-testid="email-prefs-list">
                  {[
                    { key: 'proposals', label: 'Propuestas', desc: 'Cuando recibes una nueva propuesta en tu cotización', icon: '📩' },
                    { key: 'quotations', label: 'Cotizaciones', desc: 'Cuando se publica una cotización en tus especialidades', icon: '📋' },
                    { key: 'chat_messages', label: 'Mensajes de Chat', desc: 'Cuando recibes un nuevo mensaje en el chat', icon: '💬' },
                    { key: 'ratings', label: 'Calificaciones', desc: 'Cuando alguien califica tu servicio', icon: '⭐' },
                    { key: 'commissions', label: 'Ventas y Comisiones', desc: 'Cuando se completa una venta o se paga una comisión', icon: '✅' },
                    { key: 'marketing', label: 'Novedades y Promociones', desc: 'Actualizaciones de la plataforma y ofertas especiales', icon: '📢' },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex items-center justify-between p-4 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                      data-testid={`email-pref-${item.key}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xl flex-shrink-0">{item.icon}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{item.label}</p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{item.desc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={emailPrefs[item.key] ?? true}
                        onCheckedChange={() => handleTogglePref(item.key)}
                        disabled={savingPrefs}
                        data-testid={`email-pref-toggle-${item.key}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch className="w-6 h-6 animate-spin text-zinc-400" />
                </div>
              )}

              {/* Test Email Section */}
              {emailConfigured && (
                <div className="border-t border-zinc-200 dark:border-zinc-700 pt-6">
                  <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-2 flex items-center gap-2">
                    <PaperPlaneTilt className="w-4 h-4" />
                    Correo de Prueba
                  </h4>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
                    Envía un correo de bienvenida de prueba a <strong>{user?.email}</strong> para verificar que la configuración funciona correctamente.
                  </p>
                  <Button
                    variant="outline"
                    className="rounded-none border-orange-300 text-orange-600 hover:bg-orange-50"
                    onClick={handleSendTestEmail}
                    disabled={sendingTest}
                    data-testid="send-test-email-btn"
                  >
                    {sendingTest ? (
                      <CircleNotch className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <PaperPlaneTilt className="w-4 h-4 mr-2" />
                    )}
                    {sendingTest ? 'Enviando...' : 'Enviar Correo de Prueba'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Legal Tab — free consultation of legal documents (mirrors mobile app) */}
        <TabsContent value="legal">
          <Card className="border-zinc-200 rounded-none" data-testid="legal-card">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-500" />
                Documentos Legales
              </CardTitle>
              <p className="text-sm text-zinc-500 mt-1">
                Consulta en cualquier momento los documentos vigentes que regulan el uso de MaterMatch.
                También puedes descargarlos o imprimirlos desde tu navegador.
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-3">
              {[
                {
                  key: 'terms',
                  title: 'Términos y Condiciones',
                  desc: 'Reglas de uso de la plataforma, proceso de cotización, comisiones y responsabilidades.',
                  path: '/api/legal/terms-conditions/page',
                  testid: 'legal-terms-link',
                },
                {
                  key: 'privacy',
                  title: 'Aviso de Privacidad',
                  desc: 'Qué datos recabamos, para qué los usamos, tus derechos ARCO y cómo ejercerlos.',
                  path: '/api/legal/privacy-policy/page',
                  testid: 'legal-privacy-link',
                },
                {
                  key: 'data-deletion',
                  title: 'Política de Eliminación de Cuenta',
                  desc: 'Cómo solicitar la eliminación de tus datos, qué se elimina y qué se conserva por obligación fiscal.',
                  path: '/api/legal/data-deletion/page',
                  testid: 'legal-data-deletion-link',
                },
              ].map((doc) => (
                <a
                  key={doc.key}
                  href={doc.path}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={doc.testid}
                  className="flex items-center gap-4 p-4 border border-zinc-200 hover:border-orange-300 hover:bg-orange-50/40 transition-colors group"
                >
                  <div className="w-10 h-10 bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                    <FileText className="w-5 h-5" weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-zinc-900 group-hover:text-orange-700 transition-colors">
                      {doc.title}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5 leading-snug">
                      {doc.desc}
                    </p>
                  </div>
                  <DownloadSimple className="w-5 h-5 text-zinc-400 group-hover:text-orange-600 flex-shrink-0" />
                </a>
              ))}

              {/* Acceptance proof block */}
              {user?.legal_acceptance?.accepted_at && (
                <div className="mt-6 pt-5 border-t border-zinc-200" data-testid="legal-acceptance-status">
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                    Tu aceptación
                  </p>
                  <div className="flex items-start gap-2 text-sm text-zinc-700">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" weight="fill" />
                    <div>
                      <p>
                        Aceptaste los tres documentos{' '}
                        <span className="font-medium">
                          {new Date(user.legal_acceptance.accepted_at).toLocaleString('es-MX', {
                            year: 'numeric', month: 'long', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </span>.
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Versión vigente: <code className="text-zinc-700">{user.legal_acceptance.version || '—'}</code>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-zinc-400 italic pt-4">
                ¿Dudas? Escríbenos a{' '}
                <a href="mailto:contacto@matermatch.com" className="underline hover:text-orange-600">
                  contacto@matermatch.com
                </a>
                .
              </p>
            </CardContent>
          </Card>

          {/* Danger Zone — Account deletion */}
          <Card className="border-2 border-red-300 dark:border-red-700 rounded-none mt-6" data-testid="danger-zone-card">
            <CardHeader className="border-b-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20">
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <ShieldWarning className="w-5 h-5" weight="fill" />
                Zona de Peligro
              </CardTitle>
              <p className="text-sm text-red-700/80 dark:text-red-300/80 mt-1">
                Acciones permanentes e irreversibles. Procede con precaución.
              </p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-zinc-900 dark:text-zinc-100">Eliminar mi cuenta</p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1 leading-relaxed">
                    Elimina permanentemente tu perfil, tus datos personales y todos tus proyectos.
                    Esta acción <span className="font-bold text-red-700 dark:text-red-400">no puede deshacerse</span>.
                    Los registros fiscales se conservarán de forma anonimizada por obligación legal.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="rounded-none bg-white hover:bg-red-600 hover:text-white text-red-700 border-2 border-red-600 h-11 px-5 font-semibold transition-colors flex-shrink-0"
                  data-testid="open-delete-account-btn"
                >
                  <Trash className="w-4 h-4 mr-2" weight="bold" />
                  Eliminar mi cuenta
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DeleteAccountDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </div>
  );
};

export default ProfilePage;
